import * as React from 'react';

import {
  enumFacilitySortOption,
  type FacilitySortOption,
} from '@rematter/frontend-lib-api-client';
import {
  Col,
  Row,
  SearchField,
  TinyTable,
  Pagination,
  type PaginationProps,
  type TinyTableSorting,
} from '@rematter/ui-web';
import { useAtom } from 'jotai';
import { LucideHash, LucideMapPin, LucideType } from 'lucide-react';

import { useFacilitiesQuery } from './_graphql/useFacilitiesQuery';
import { useFacilityCountsQuery } from './_graphql/useFacilityCountsQuery';
import { FacilityRecord } from './types';

import { SettingsContent } from '~/modules/settings/_components/_shared';
import { useSettingsLocation } from '~/modules/settings/_state/settingsModule.state';
import * as state from '~/modules/settings/_state/teamPermissions.state';
import { useTranslations } from '~/modules/settings/i18n/useTranslations';
import useDebouncedValue from '~/shared/hooks/useDebounce';

export const FacilitiesTabView = () => {
  const t = useTranslations();
  const [, setLocation] = useSettingsLocation();

  const [searchTerm, setSearchTerm] = React.useState('');
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);

  const [offset, setOffset] = useAtom(state.$facilitiesOffset);
  const [limit, setLimit] = useAtom(state.$facilitiesLimit);
  const [sorting, setSorting] = React.useState<TinyTableSorting>({
    column: 'name',
    direction: 'asc',
  });

  const serverSorting = React.useMemo((): FacilitySortOption[] => {
    if (sorting.column === 'name') {
      return [
        sorting.direction === 'asc'
          ? enumFacilitySortOption.NameAsc
          : enumFacilitySortOption.NameDesc,
      ];
    }
    return [enumFacilitySortOption.NameAsc];
  }, [sorting]);

  const {
    data: { facilities: facilitiesData = [], total = 0 } = {
      facilities: [],
      total: 0,
    },
    isLoading,
    error,
  } = useFacilitiesQuery({
    searchTerm: debouncedSearchTerm || undefined,
    sorting: serverSorting,
    limit,
    offset,
  });

  const { data: facilityCountsData } = useFacilityCountsQuery({});

  const facilities = React.useMemo(() => {
    const counts = facilityCountsData?.counts ?? {};
    const merged = facilitiesData.map((facility) => ({
      ...facility,
      userCount: counts[facility.id]?.userCount ?? 0,
      roleCount: counts[facility.id]?.roleCount ?? 0,
    }));

    if (sorting.column === 'name') return merged;

    return [...merged].sort((a, b) => {
      const dir = sorting.direction === 'asc' ? 1 : -1;
      switch (sorting.column) {
        case 'address':
          return dir * a.address.localeCompare(b.address);
        case 'userCount':
          return dir * (a.userCount - b.userCount);
        case 'roleCount':
          return dir * (a.roleCount - b.roleCount);
        default:
          return 0;
      }
    });
  }, [facilitiesData, facilityCountsData, sorting]);

  const handlePaginationChange: PaginationProps['onChange'] = ({
    limit: newLimit,
    offset: newOffset,
  }) => {
    setLimit(newLimit);
    setOffset(newOffset);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setOffset(0);
  };

  const handleSortChange = (newSorting: TinyTableSorting) => {
    setSorting(newSorting);
    if (newSorting.column === 'name') {
      setOffset(0);
    }
  };

  return (
    <SettingsContent className="@FacilitiesTabView h-full">
      <Col className="h-full gap-4">
        <Row gap2>
          <SearchField
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder={t.teamAndPermissions.search.placeholder}
            autoFocus
          />
        </Row>

        <TinyTable<FacilityRecord>
          rowKey={(row) => row.id}
          selectable
          isLoading={isLoading}
          error={error ?? undefined}
          sorting={sorting}
          onSortChange={handleSortChange}
          onRowClick={(row) =>
            setLocation(`/team-and-permissions/facilities/${row.id}` as never)
          }
          columns={[
            {
              key: 'name',
              label: t.teamAndPermissions.facilities.table.columns.name,
              icon: LucideType,
              sortable: true,
              cell: (row) => row.name,
            },
            {
              key: 'address',
              label: t.teamAndPermissions.facilities.table.columns.address,
              icon: LucideMapPin,
              sortable: true,
              cell: (row) => (
                <div className="flex w-full">
                  <div className="w-0 grow truncate">{row.address}</div>
                </div>
              ),
            },
            {
              key: 'userCount',
              label: t.teamAndPermissions.facilities.table.columns.users,
              icon: LucideHash,
              width: 120,
              sortable: true,
              cell: (row) => row.userCount,
            },
            {
              key: 'roleCount',
              label: t.teamAndPermissions.facilities.table.columns.roles,
              icon: LucideHash,
              width: 140,
              sortable: true,
              cell: (row) => row.roleCount,
            },
          ]}
          data={facilities}
        />

        {total > 0 && (
          <Row className="justify-end">
            <Pagination
              limit={limit}
              offset={offset}
              total={total}
              onChange={handlePaginationChange}
            />
          </Row>
        )}
      </Col>
    </SettingsContent>
  );
};
